import * as yup from "yup";
import dayjs from "dayjs";

// Email regex that's case insensitive
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;

// Username regex that's case insensitive
const usernameRegex = /^[a-zA-Z0-9]+$/i;

export const loginFormSchema = yup.object().shape({
  username: yup
    .string()
    .required("Username is required")
    .test("username-or-email", "Invalid Username", function (value) {
      if (!value) return false;

      // Trim the value before validation
      const trimmedValue = value.trim();

      if (emailRegex.test(trimmedValue)) {
        return true;
      }

     
      //checking if it's a valid username
      const hasLetter = /[a-zA-Z]/.test(trimmedValue);
      const hasNumber = /\d/.test(trimmedValue);
      const isValidUsername =
        trimmedValue.length >= 6 &&
        trimmedValue.length <= 35 &&
        usernameRegex.test(trimmedValue) &&
        !/[\W_]/.test(trimmedValue);

      if (isValidUsername) {
        if (!hasLetter) {
          return this.createError({
            message: "Username must contain at least one letter",
          });
        }
        if (!hasNumber) {
          return this.createError({
            message: "Username must contain at least one number",
          });
        }
        return true;
      }

      return false;
    }),
    password: yup
    .string()
    .required("Password is required")
    .min(8, "Password should be at least 8 characters")
    .max(20, "Password should not exceed 20 characters")
    .matches(/[\W_]/, "Password must contain at least one special character")
    .notOneOf([" "], "Password should not contain spaces")
    .matches(/^[^ ]/, "Password should not start with a space"),
});

//create-account form schema
export const createAccountFormSchema = yup.object().shape({
  firstName: yup
    .string()
    .required("First name is required")
    .min(1, "First name should be at least 1 character")
    .max(30, "First name should not exceed 30 characters")
    .matches(/^[A-Za-z ]*$/, "Alphabetic characters only")
    .test(
      "not-equal-to-lastName",
      "First name and Last name should not be equal",
      function (value) {
        return value !== this.parent.lastName;
      }
    ),

  lastName: yup
    .string()
    .required("Last name is required")
    .min(1, "Last name should be at least 1 character")
    .max(30, "Last name should not exceed 30 characters")
    .matches(/^[A-Za-z ]*$/, "Alphabetic characters only")
    .test(
      "not-equal-to-firstName",
      "First name and Last name should not be equal",
      function (value) {
        return value !== this.parent.firstName;
      }
    ),

  dateOfBirth: yup
    .mixed() // Use mixed() instead of date() to handle custom validation
    .test("is-valid-date", "Incorrect date of birth", function (value) {
      if (!value) return false; // Handle empty values

      const dateObj = dayjs(value, "DD/MM/YYYY", true); // Use strict parsing
      return dateObj.isValid(); // Check if the date is valid
    })
    .test("is-past-date", "Date of Birth cannot be in the future", function (value) {
      if (!value) return false;

      const dateObj = dayjs(value, "DD/MM/YYYY", true);
      const today = dayjs();
      return dateObj.isBefore(today); // Check if the date is in the past
    })
    .test("is-old-enough", "The student needs to be 6 years old to enroll", function (value) {
      if (!value) return false;

      const dateObj = dayjs(value, "DD/MM/YYYY", true);
      const today = dayjs();
      let age = today.year() - dateObj.year();
      const monthDiff = today.month() - dateObj.month();
      if (monthDiff < 0 || (monthDiff === 0 && today.date() < dateObj.date())) {
        age--;
      }
      return age >= 6; // Check if the user is at least 6 years old
    })
    .required("Date of Birth is required"),
});

export const createAccountFormSchemaPublic = yup.object().shape({
  firstName: yup
    .string()
    .required("First name is required")
    .min(1, "First name should be at least 1 character")
    .max(30, "First name should not exceed 30 characters")
    .matches(/^[A-Za-z ]*$/, "Alphabetic characters only")
    .test(
      "not-equal-to-lastName",
      "First name and Last name should not be equal",
      function (value) {
        return value !== this.parent.lastName;
      }
    ),

  lastName: yup
    .string()
    .required("Last name is required")
    .min(1, "Last name should be at least 1 character")
    .max(30, "Last name should not exceed 30 characters")
    .matches(/^[A-Za-z ]*$/, "Alphabetic characters only")
    .test(
      "not-equal-to-firstName",
      "First name and Last name should not be equal",
      function (value) {
        return value !== this.parent.firstName;
      }
    ),

  email: yup
    .string()
    .required("Email is required")
    .matches(emailRegex, "Enter a valid email")
    .trim(),

  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password should be at least 8 characters")
    .max(20, "Password should not exceed 20 characters")
    .matches(/[\W_]/, "Password must contain at least one special character")
    .notOneOf([" "], "Password should not contain spaces")
    .matches(/^[^ ]/, "Password should not start with a space"),

});
// schema for forgot password

export const forgotPasswordFormSchema = yup.object().shape({
  username: yup
    .string()
    .required("Username or email required")
    .test("username-or-email", "Invalid Username", function (value) {
      if (!value) return false;

      // Convert to lowercase before validation
      const trimmedValue = value.trim().toLowerCase();

      // checking if it's a valid email
      if (emailRegex.test(trimmedValue)) {
        return true;
      }

      // Check if it's a valid username (case insensitive)
      const hasLetter = /[a-zA-Z]/i.test(trimmedValue);
      const hasNumber = /\d/.test(trimmedValue);
      const isValidUsername =
        trimmedValue.length >= 6 &&
        trimmedValue.length <= 35 &&
        usernameRegex.test(trimmedValue);

      if (isValidUsername) {
        if (!hasLetter) {
          return this.createError({
            message: "Username must contain at least one letter",
          });
        }
        if (!hasNumber) {
          return this.createError({
            message: "Username must contain at least one number",
          });
        }
        return true;
      }

      return false;
    }),
});

//reset password schema
export const resetPasswordFormSchema = yup.object().shape({
  newPassword: yup
    .string()
    .required("Password is required")
    .min(8, "Password should be at least 8 characters")
    .max(20, "Password should not exceed 20 characters")
    .matches(/[\W_]/, "Password must contain at least one special character")
    .notOneOf([" "], "Password should not contain spaces")
    .matches(/^[^ ]/, "Password should not start with a space"),

  confirmPassword: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("newPassword"), null], "Passwords must match"),
});

//date of birth schema
export const dateOfBirthFormSchema = yup.object().shape({
  dateOfBirth: yup
    .date()
    .typeError("Invalid date")
    .required("Date of Birth is required")
    .test(
      "ageRestriction",
      "Students should be at least 6 years old to enroll",
      function (value) {
        const today = new Date();
        let age = today.getFullYear() - value.getFullYear();
        const monthDiff = today.getMonth() - value.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < value.getDate())
        ) {
          age--;
        }

        return age >= 6;
      }
    )
    .max(new Date(), "Date of Birth cannot be in the future"),
});